import { Loader } from "@mantine/core"
import { IconEdit } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { CountryIso3166 } from "@tfg/db"
import { ActionIcon, Missing, CountryCell, TextCell } from "@tfg/ui"
import { Dispatch, SetStateAction, useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  CreateBusinessPartnerRequestDto,
  FindContactResponseDto,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import DeleteContact from "~/pages/Brand/Suppliers/SuppliersRepository/components/DeleteContact"
import { COUNTRIES } from "~/utils/countries"

function constructSupplierAddress(
  street: string | null,
  city: string | null,
  postalCode: string | null
) {
  if (
    !street ||
    !city ||
    street === "not-entered-yet" ||
    city === "not-entered-yet"
  ) {
    return <Missing />
  }
  const addressParts = [street, city]
  if (postalCode) {
    addressParts.push(postalCode)
  }
  const address = addressParts.join(", ")
  return address.endsWith(",") ? address.slice(0, -1) : address
}

export const useColumns = ({
  setInitialValues,
  setValuesUpdated,
  setFormType,
}: {
  setInitialValues: Dispatch<
    SetStateAction<
      | CreateBusinessPartnerRequestDto
      | (UpdateBusinessPartnerRequestDto & { id: number })
    >
  >
  setValuesUpdated: Dispatch<SetStateAction<boolean>>
  setFormType: Dispatch<SetStateAction<"create" | "update">>
}) => {
  const { t } = useTranslation("traceability")
  const columnHelper = createColumnHelper<FindContactResponseDto>()

  return useMemo<ColumnDef<FindContactResponseDto, any>[]>(() => {
    const update = async (values: FindContactResponseDto) => {
      setInitialValues((prev) => ({
        ...prev,
        id: values.id,
        supplierId: values.supplier.id,
        name: values.supplier.name,
        email: values.supplier.email,
        preferedLangageIso6391: values.supplier.preferedLangageIso6391,
        organization: {
          businessRelationship: {
            isActive:
              values.supplier.organization.businessRelationship.isActive,
            supplierCode:
              values.supplier.organization.businessRelationship.supplierCode ??
              "",
            type:
              values.supplier.organization.businessRelationship.type ?? null,
            tagNames:
              values.supplier.organization.businessRelationship.tagNames ?? [],
          },
          id: values.supplier.organization.id,
          name: values.supplier.organization.name,
          street:
            values.supplier.organization.street !== "not-entered-yet"
              ? (values.supplier.organization.street ?? "")
              : "",
          city:
            values.supplier.organization.city !== "not-entered-yet"
              ? (values.supplier.organization.city ?? "")
              : "",
          postalCode: values.supplier.organization.postalCode ?? "",
          country: values.supplier.organization.country ?? CountryIso3166.FR,
          registrationId: values.supplier.organization.registrationId ?? [],
        },
      }))
      setValuesUpdated(true)
      setFormType("update")
    }

    return [
      columnHelper.accessor("supplier.name", {
        header: "Supplier",
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("supplier.email", {
        header: "Email",
        id: "email",
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("supplier.organization.name", {
        header: "Company",
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor(
        (originalRow) =>
          originalRow.supplier.organization.businessRelationship.supplierCode ??
          "",
        {
          id: "supplierCode",
          header: "Supplier Code",
          enableSorting: true,
          cell: ({ getValue }) => {
            const value = getValue()
            return value ? <TextCell value={value} /> : <Missing />
          },
        }
      ),
      columnHelper.accessor(
        (originalRow) =>
          originalRow.supplier.organization.businessRelationship.tagNames ?? [],
        {
          id: "tags",
          header: t("contactModal.labels.tags"),
          enableSorting: true,
          cell: ({ getValue }) => {
            const tags = getValue()
            return tags.length > 0 ? (
              <TextCell value={tags.join(", ")} />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor(
        (originalRow) =>
          constructSupplierAddress(
            originalRow.supplier.organization.street,
            originalRow.supplier.organization.city,
            originalRow.supplier.organization.postalCode
          ),
        {
          id: "address",
          header: "Address",
          enableSorting: false,
          cell: ({ getValue }) => <TextCell value={getValue()} />,
        }
      ),

      columnHelper.accessor("supplier.organization.country", {
        header: "Country",
        enableSorting: false,
        cell: (info) => {
          return (
            <CountryCell
              countryIso3166={info.getValue()}
              countriesMapper={COUNTRIES}
            />
          )
        },
      }),
      columnHelper.display({
        header: undefined,
        id: "action-cell-delete",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return original?.supplier ? (
            <DeleteContact id={original.id} />
          ) : (
            <Loader />
          )
        },
      }),
      columnHelper.display({
        id: "action-cell-edit",
        header: undefined,
        cell: ({ row: { original } }) => {
          return (
            <ActionIcon icon={<IconEdit />} onClick={() => update(original)} />
          )
        },
      }),
    ]
  }, [columnHelper, setFormType, setInitialValues, setValuesUpdated, t])
}
